import { logError } from "@/lib/log";
import { AllergenBadges, DietBadges } from "@/components/Allergens";
import { ALLERGENS, DIET_TAGS } from "@/lib/allergens";
import { useCallback, useEffect, useRef, useState, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { loadGoogleFont } from "@/lib/fonts";
import { fmtMoney } from "@/lib/money";
import { motion } from "framer-motion";
import { MapPin, Phone, CalendarCheck, Utensils, Loader2, CreditCard, ShoppingBag, Plus, Minus, X, Clock, Trash2, Sun, ShoppingBasket, Truck, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { AdSlot } from "@/components/AdSlot";
import { AdsConsentBanner, getAdsConsent } from "@/components/AdsConsentBanner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";

const TEMPLATES = {
  elegante: { accent: "#5aa469", pageBg: "#FAFBFA", heading: "font-display font-bold", pill: "rounded-full", uppercase: false },
  moderno: { accent: "#4338ca", pageBg: "#f5f5f7", heading: "font-sans font-extrabold tracking-tight", pill: "rounded-md", uppercase: true },
  minimal: { accent: "#111827", pageBg: "#ffffff", heading: "font-sans font-semibold", pill: "rounded-full", uppercase: false },
  colorato: { accent: "#f97316", pageBg: "#fff7ed", heading: "font-display font-bold", pill: "rounded-full", uppercase: false },
};

export default function PublicMenu() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [activeMenu, setActiveMenu] = useState(null);
  const [ftView, setFtView] = useState(false);
  const [adsConsent, setAdsConsent] = useState(getAdsConsent());
  const [payOpen, setPayOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [table, setTable] = useState("");
  const [paying, setPaying] = useState(false);
  const [configDish, setConfigDish] = useState(null);
  const [cart, setCart] = useState([]);
  const [cartOpen, setCartOpen] = useState(false);
  const [orderMode, setOrderMode] = useState("table");
  const [filterAllergens, setFilterAllergens] = useState([]);
  const [dietFilter, setDietFilter] = useState([]);
  const [lang, setLang] = useState("it");
  const [tr, setTr] = useState({});
  const [trLoading, setTrLoading] = useState(false);
  const switchLang = async (l) => {
    setLang(l);
    window.dispatchEvent(new CustomEvent("menuone-lang", { detail: l }));
    if (l === "it" || tr[l]) return;
    setTrLoading(true);
    try { const { data: t } = await api.get(`/public/menu/${slug}/translate`, { params: { lang: l } }); setTr((s) => ({ ...s, [l]: t })); }
    catch (e) { toast.error("Traduzione non disponibile al momento"); setLang("it"); window.dispatchEvent(new CustomEvent("menuone-lang", { detail: "it" })); }
    finally { setTrLoading(false); }
  };
  const tName = (d) => (lang !== "it" && tr[lang]?.dishes?.[d.id]?.name) || d.name;
  const tDesc = (d) => (lang !== "it" && tr[lang]?.dishes?.[d.id] ? tr[lang].dishes[d.id].description : d.description);
  const tCat = (c) => (lang !== "it" && tr[lang]?.categories?.[c.id]) || c.name;
  const autoLangDone = useRef(false);
  useEffect(() => {
    if (!data?.translate_enabled || autoLangDone.current) return;
    autoLangDone.current = true;
    const saved = localStorage.getItem("menuone_lang");
    const pick = saved || "it";
    if (pick !== "it" && (data.languages || []).some((x) => x.code === pick)) switchLang(pick);
  }, [data]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (autoLangDone.current) localStorage.setItem("menuone_lang", lang); }, [lang]);
  useEffect(() => { if (data?.heading_font_enabled && data.restaurant?.heading_font) loadGoogleFont(data.restaurant.heading_font); }, [data]);
  const [waitOpen, setWaitOpen] = useState(false);
  const [resOpen, setResOpen] = useState(false);

  const loadMenu = useCallback(async () => {
    try {
      const res = await api.get(`/public/menu/${slug}`);
      setData(res.data);
      document.title = `${res.data.restaurant.name} · MenuOne`;
      setActiveMenu((prev) => (prev && res.data.menus.some((m) => m.id === prev) ? prev : res.data.menus[0]?.id));
    } catch (e) { logError("Caricamento menù pubblico fallito:", e); setError(e.response?.status === 403 ? "suspended" : true); }
  }, [slug]);
  useEffect(() => { loadMenu(); }, [loadMenu]);

  const embedMode = useRef(new URLSearchParams(window.location.search).get("mode"));
  useEffect(() => {
    if (!data || !embedMode.current) return;
    const m = embedMode.current; embedMode.current = null;
    if (m === "prenota" && data.reservations_enabled) setResOpen(true);
    if (m === "takeaway" && data.takeaway_enabled) { setOrderMode("takeaway"); toast.info("Scegli i piatti e conferma il ritiro d'asporto."); }
  }, [data]);

  useEffect(() => {
    if (searchParams.get("paid") === "1") {
      const sid = searchParams.get("session_id");
      if (sid) { api.get(`/public/pay/confirm/${sid}`).catch((e) => logError("Conferma pagamento fallita:", e)); }
      toast.success("Pagamento riuscito! Grazie 🙏");
      searchParams.delete("paid");
      searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("pay_cancel") === "1") {
      toast.info("Pagamento annullato.");
      searchParams.delete("pay_cancel");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("order_paid") === "1") {
      const sid = searchParams.get("session_id");
      if (sid) {
        api.get(`/public/order/confirm/${sid}`).then(({ data: o }) => {
          const when = o.ready_at && o.ready_at !== o.pickup_time ? o.ready_at : o.pickup_time;
          toast.success(o.paid ? `Ordine #${o.order_no} confermato e pagato! ${o.fulfillment === "delivery" ? "Consegna" : "Ritiro"} alle ${when}.` : `Ordine #${o.order_no}: pagamento in verifica, riceverai conferma dal locale.`, { duration: 9000 });
        }).catch((e) => logError("Conferma ordine fallita:", e));
      }
      searchParams.delete("order_paid"); searchParams.delete("session_id");
      setSearchParams(searchParams, { replace: true });
    } else if (searchParams.get("order_pay_cancel") === "1") {
      const oid = searchParams.get("order");
      if (oid) api.post(`/public/order/cancel-unpaid/${oid}`).catch(() => {});
      toast.info("Pagamento annullato: l'ordine non è stato confermato.");
      searchParams.delete("order_pay_cancel"); searchParams.delete("order");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const submitPay = async () => {
    const value = parseFloat(String(amount).replace(",", "."));
    if (!value || value <= 0) { toast.error("Inserisci un importo valido"); return; }
    setPaying(true);
    try {
      const { data: res } = await api.post(`/public/pay/${slug}`, { amount: value, table, origin_url: window.location.origin, lang });
      window.location.href = res.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPaying(false);
    }
  };

  const addToCart = (line) => {
    setCart((c) => {
      const same = c.find((l) => l.dish_id === line.dish_id && (l.variant || null) === (line.variant || null) && JSON.stringify(l.options || []) === JSON.stringify(line.options || []) && (l.notes || "") === (line.notes || ""));
      if (same) return c.map((l) => (l === same ? { ...l, quantity: l.quantity + line.quantity } : l));
      return [...c, { ...line, key: Date.now() + Math.random() }];
    });
    toast.success(`${line.name} aggiunto`);
  };
  const setQty = (key, delta) => setCart((c) => c.map((l) => (l.key === key ? { ...l, quantity: Math.max(1, l.quantity + delta) } : l)));
  const removeLine = (key) => setCart((c) => c.filter((l) => l.key !== key));
  const cartCount = cart.reduce((n, l) => n + l.quantity, 0);
  const cartTotal = cart.reduce((n, l) => n + l.unit_price * l.quantity, 0);

  if (error) return (
    <div className="min-h-screen flex flex-col items-center justify-center gap-4 px-6 text-center bg-white text-gray-900">
      <Logo />
      <p className="text-gray-500" data-testid="public-menu-error">{error === "suspended" ? "Questo menù è temporaneamente non disponibile." : "Menù non trovato."}</p>
    </div>
  );
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  const { restaurant, categories, dishes, menus } = data;
  const ftMenuActive = ftView && !!data.foodtruck_menu;
  const shownMenuId = ftMenuActive ? data.foodtruck_menu.id : activeMenu;
  const currentMenu = ftMenuActive ? data.foodtruck_menu : (menus.find((m) => m.id === activeMenu) || menus[0]);
  const baseT = TEMPLATES[currentMenu?.template] || TEMPLATES.elegante;
  const t = data.accent_color_enabled && restaurant.accent_color ? { ...baseT, accent: restaurant.accent_color } : baseT;
  const headingFont = data.heading_font_enabled && restaurant.heading_font ? restaurant.heading_font : null;
  const hStyle = headingFont ? { fontFamily: `"${headingFont}", serif` } : undefined;
  const menuCategories = categories.filter((c) => c.menu_id === shownMenuId);
  const allergenCodesUsed = Array.from(new Set(dishes.flatMap((d) => d.allergens || [])));
  const hasAllergen = (d) => filterAllergens.length > 0 && (d.allergens || []).some((c) => filterAllergens.includes(c));
  const highlightedCount = dishes.filter(hasAllergen).length;
  const dietTagsUsed = Array.from(new Set(dishes.flatMap((d) => d.tags || [])));
  const matchesDiet = (d) => dietFilter.length === 0 || dietFilter.every((t) => (d.tags || []).includes(t));
  const cover = restaurant.cover_path ? fileUrl(restaurant.cover_path) : "https://images.unsplash.com/photo-1447078806655-40579c2520d6?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
  const embedded = new URLSearchParams(window.location.search).get("embed") === "1";

  return (
    <div className="min-h-screen text-gray-900" style={{ background: t.pageBg }} data-testid="public-menu">
      <div className="max-w-md mx-auto bg-white min-h-screen shadow-sm">
        <div className={`relative ${embedded ? "h-32" : "h-52"}`}>
          <img src={cover} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
          {data.branding && (
            <a href="/" className="absolute top-4 right-4 inline-flex items-center gap-1.5 bg-white/95 rounded-full pl-2 pr-3 py-1 text-[11px] font-semibold text-gray-700 shadow" data-testid="menuone-branding-badge">
              <Logo className="[&_img]:h-4" /> <span>Creato con MenuOne</span>
            </a>
          )}
          <div className="absolute bottom-0 inset-x-0 p-6 text-white">
            {restaurant.logo_path && (
              <div className="h-16 w-16 rounded-2xl overflow-hidden border-2 border-white/70 mb-3 bg-white">
                <img src={fileUrl(restaurant.logo_path)} alt="" className="h-full w-full object-cover" />
              </div>
            )}
            <h1 className={`${t.heading} text-4xl leading-tight`} style={hStyle} data-testid="menu-title" data-no-translate>{restaurant.name}</h1>
          </div>
        </div>

        {(restaurant.description || restaurant.address || restaurant.phone) && (
          <div className="px-6 py-5 border-b border-gray-100 space-y-2">
            {restaurant.description && <p className="text-gray-600 leading-relaxed" data-no-translate>{restaurant.description}</p>}
            <div className="flex flex-col gap-1.5 pt-1">
              {restaurant.address && <span className="inline-flex items-center gap-2 text-sm text-gray-500" data-no-translate><MapPin className="h-4 w-4" style={{ color: t.accent }} />{restaurant.address}</span>}
              {restaurant.phone && <a href={`tel:${restaurant.phone}`} className="inline-flex items-center gap-2 text-sm text-gray-500"><Phone className="h-4 w-4" style={{ color: t.accent }} />{restaurant.phone}</a>}
              {restaurant.booking_contact && <a href={`tel:${restaurant.booking_contact}`} className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: t.accent }}><CalendarCheck className="h-4 w-4" />Prenota: {restaurant.booking_contact}</a>}
            </div>
          </div>
        )}

        {data.translate_enabled && (
          <div className="px-4 pt-3 flex items-center justify-end gap-1" data-testid="lang-switcher">
            {trLoading && <Loader2 className="h-3.5 w-3.5 animate-spin text-gray-400 mr-1" />}
            <div className="flex gap-1 overflow-x-auto no-scrollbar max-w-full" data-testid="lang-list">
              {[...(data.languages || [])].sort((a, b) => (a.code === lang ? -1 : b.code === lang ? 1 : a.code === "it" ? -1 : b.code === "it" ? 1 : 0)).map(({ code: l, label }) => (
                <button key={l} onClick={() => switchLang(l)} className="text-xs font-semibold px-2.5 py-1 rounded-full border transition-colors shrink-0"
                  style={lang === l ? { background: t.accent, borderColor: t.accent, color: "#fff" } : { borderColor: "#e5e7eb", color: "#4b5563" }} data-testid={`lang-${l}`}>{label}</button>
              ))}
            </div>
          </div>
        )}

        {/* Menu switcher */}
        {menus.length > 1 && !ftMenuActive && (
          <div className="px-4 py-3 flex gap-2 overflow-x-auto border-b border-gray-100" data-testid="public-menu-switcher">
            {menus.map((m) => (
              <button key={m.id} onClick={() => setActiveMenu(m.id)} data-testid={`public-menu-tab-${m.id}`}
                className={`whitespace-nowrap text-sm px-4 py-1.5 ${t.pill} font-medium transition-colors`}
                style={activeMenu === m.id ? { background: t.accent, color: "#fff" } : { background: "#f3f4f6", color: "#4b5563" }}>
                {m.name}
              </button>
            ))}
          </div>
        )}

        {data.allergen_filter_enabled && allergenCodesUsed.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100" data-testid="allergen-filter">
            <div className="flex items-center justify-between mb-2">
              <p className="text-[11px] font-bold uppercase tracking-wide text-amber-700">Evidenzia piatti con allergene</p>
              {filterAllergens.length > 0 && <button onClick={() => setFilterAllergens([])} className="text-xs text-gray-500 underline" data-testid="allergen-filter-clear">Azzera</button>}
            </div>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {ALLERGENS.filter((a) => allergenCodesUsed.includes(a.code)).map((a) => {
                const on = filterAllergens.includes(a.code);
                return (
                  <button key={a.code} onClick={() => setFilterAllergens((s) => (on ? s.filter((c) => c !== a.code) : [...s, a.code]))} aria-pressed={on}
                    className={`whitespace-nowrap text-xs px-3 py-1.5 rounded-full border font-medium transition-colors ${on ? "bg-amber-500 border-amber-500 text-white" : "border-amber-200 text-amber-800 bg-amber-50"}`} data-testid={`allergen-filter-${a.code}`}>{a.label}</button>
                );
              })}
            </div>
            {filterAllergens.length > 0 && <p className="text-xs text-gray-500 mt-2" data-testid="allergen-filter-count">{highlightedCount} {highlightedCount === 1 ? "piatto contiene" : "piatti contengono"} gli allergeni selezionati</p>}
          </div>
        )}

        {data.diet_filter_enabled && dietTagsUsed.length > 0 && (
          <div className="px-4 py-3 border-b border-gray-100" data-testid="diet-filter">
            <p className="text-[11px] font-bold uppercase tracking-wide mb-2" style={{ color: t.accent }}>Preferenze alimentari</p>
            <div className="flex gap-2 overflow-x-auto pb-1">
              {DIET_TAGS.filter((x) => dietTagsUsed.includes(x.code)).map((x) => {
                const on = dietFilter.includes(x.code);
                return (
                  <button key={x.code} onClick={() => setDietFilter((s) => (on ? s.filter((c) => c !== x.code) : [...s, x.code]))} aria-pressed={on}
                    className="whitespace-nowrap text-xs px-3 py-1.5 rounded-full border font-medium transition-colors"
                    style={on ? { background: t.accent, borderColor: t.accent, color: "#fff" } : { borderColor: "#e5e7eb", color: "#374151", background: "#fff" }} data-testid={`diet-filter-${x.code}`}>{x.label}</button>
                );
              })}
              {dietFilter.length > 0 && <button onClick={() => setDietFilter([])} className="text-xs text-gray-500 underline whitespace-nowrap" data-testid="diet-filter-clear">Mostra tutto</button>}
            </div>
          </div>
        )}

        {data.foodtruck_next && (
          <div className="px-6 pt-5 space-y-3">
            {data.foodtruck_menu && (
              <button onClick={() => setFtView((v) => !v)} className={`w-full h-12 inline-flex items-center justify-center gap-2 font-semibold ${t.pill} text-white shadow-md active:scale-[0.98] transition-transform`} style={{ background: t.accent }} data-testid="foodtruck-view-menu">
                <Truck className="h-5 w-5" /> {ftView ? "Torna al menù del locale" : `Menù FoodTruck ${data.foodtruck_next.is_today ? "di stasera" : "della serata"}: ${data.foodtruck_menu.name}`}
              </button>
            )}
            <div className="rounded-2xl p-5 border-2 flex gap-4" style={{ borderColor: t.accent, background: `${t.accent}0d` }} data-testid="foodtruck-box">
              <Truck className="h-6 w-6 shrink-0 mt-0.5" style={{ color: t.accent }} />
              <div>
                <h2 className={`${t.heading} text-xl`} style={hStyle}>{data.foodtruck_next.is_today ? "Oggi ci trovi qui" : "Prossima tappa"}</h2>
                <p className="font-medium mt-1">{data.foodtruck_next.place}</p>
                <p className="text-sm text-gray-600">
                  {data.foodtruck_next.date && new Date(`${data.foodtruck_next.date}T12:00:00`).toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long", timeZone: "Europe/Rome" })}
                  {data.foodtruck_next.time_from && ` · ${data.foodtruck_next.time_from}${data.foodtruck_next.time_to ? `–${data.foodtruck_next.time_to}` : ""}`}
                </p>
                {data.foodtruck_next.note && <p className="text-sm text-gray-500 mt-1">{data.foodtruck_next.note}</p>}
              </div>
            </div>
          </div>
        )}

        {data.daily_menu && !ftMenuActive && (
          <div className="px-6 pt-5">
            <div className="rounded-2xl p-5 border-2" style={{ borderColor: t.accent, background: `${t.accent}0d` }} data-testid="daily-menu-box">
              <div className="flex items-center justify-between mb-3">
                <h2 className={`${t.heading} text-2xl`} style={hStyle}>Menù del giorno</h2>
                <span className="inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wide text-white px-2.5 py-1 rounded-full" style={{ background: t.accent }} data-testid="daily-badge"><Sun className="h-3 w-3" /> Oggi</span>
              </div>
              {data.daily_menu.note && <p className="text-sm text-gray-600 mb-3 italic" data-testid="daily-note-public">{data.daily_menu.note}</p>}
              <div className="space-y-3">
                {data.daily_menu.items.map((it) => (
                  <div key={it.id} className="flex justify-between gap-3" data-testid={`daily-public-item-${it.id}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900" data-no-translate>{it.name}</p>
                      {it.description && <p className="text-sm text-gray-500" data-no-translate>{it.description}</p>}
                    </div>
                    {Number(it.price) > 0 && <span className="font-semibold whitespace-nowrap" style={{ color: t.accent }}>{fmtMoney(it.price, restaurant.currency_code, lang)}</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {data.takeaway_enabled && !ftMenuActive && (
          <div className="px-6 pt-4">
            <Button onClick={() => { setOrderMode("takeaway"); if (cart.length) setCartOpen(true); else toast.info("Aggiungi i piatti al carrello, poi conferma il ritiro."); }}
              className="w-full h-12 rounded-full text-white font-semibold active:scale-95 transition-transform" style={{ background: t.accent }} data-testid="takeaway-open-btn">
              <ShoppingBasket className="h-5 w-5 mr-2" /> Ordina da asporto
            </Button>
            {orderMode === "takeaway" && <p className="text-xs text-center mt-2 font-medium" style={{ color: t.accent }} data-testid="takeaway-mode-hint">Modalità asporto attiva — scegli i piatti e conferma l'orario di ritiro</p>}
          </div>
        )}

        {data.reservations_enabled && !ftMenuActive && (
          <div className="px-6 pt-4">
            <Button onClick={() => setResOpen(true)} variant="outline" className="w-full h-11 rounded-full border-gray-300" data-testid="reservation-open-btn">
              <CalendarDays className="h-4 w-4 mr-2" style={{ color: t.accent }} /> Prenota un tavolo per un altro giorno
            </Button>
          </div>
        )}

        {data.waitlist_enabled && !ftMenuActive && (
          <div className="px-6 pt-4">
            <Button onClick={() => setWaitOpen(true)} variant="outline" className="w-full h-11 rounded-full border-gray-300" data-testid="waitlist-open-btn">
              <Clock className="h-4 w-4 mr-2" style={{ color: t.accent }} /> {data.tables?.some((tb) => tb.status !== "occupied") ? `Prenota un tavolo libero (${data.tables.filter((tb) => tb.status !== "occupied").length})` : "Mettiti in lista d'attesa"}
            </Button>
          </div>
        )}

        {menuCategories.length > 1 && (
          <div id="public-menu-content" className="sticky top-0 z-10 mo-glass px-4 py-3 flex gap-2 overflow-x-auto">
            {menuCategories.map((c) => (
              <a key={c.id} href={`#cat-${c.id}`} className={`whitespace-nowrap text-sm px-3 py-1.5 ${t.pill} font-medium ${t.uppercase ? "uppercase text-xs tracking-wide" : ""}`} style={{ background: `${t.accent}1a`, color: t.accent }} data-no-translate>{c.name}</a>
            ))}
          </div>
        )}

        <div className="px-6 py-6 space-y-9">
          {ftMenuActive && (
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 text-sm text-amber-800 flex items-center gap-2" data-testid="foodtruck-menu-banner">
              <Truck className="h-4 w-4 shrink-0" /> Stai guardando il menù FoodTruck «{data.foodtruck_menu.name}»: si ordina direttamente al furgone, non dal telefono.
            </div>
          )}
          {menuCategories.length === 0 && (
            <div className="text-center py-16 text-gray-400"><Utensils className="h-10 w-10 mx-auto mb-3 text-gray-200" />Il menù è in preparazione.</div>
          )}
          {menuCategories.map((cat) => {
            const items = dishes.filter((d) => d.category_id === cat.id && matchesDiet(d));
            if (items.length === 0) return null;
            return (
              <section key={cat.id} id={`cat-${cat.id}`} className="scroll-mt-20">
                {cat.image_path && (
                  <div className="relative mb-4 overflow-hidden rounded-2xl" data-testid={`public-cat-photo-${cat.id}`}>
                    <img src={fileUrl(cat.image_path)} alt={cat.name} className="h-40 w-full object-cover" loading="lazy" />
                  </div>
                )}
                <h2 className={`${t.heading} text-3xl mb-1 ${t.uppercase ? "uppercase tracking-wide text-2xl" : ""}`} style={hStyle} data-no-translate>{tCat(cat)}</h2>
                <div className="h-0.5 w-12 mb-5 rounded-full" style={{ background: t.accent }} />
                <div className="space-y-5">
                  {items.map((d, i) => {
                    const hasVariants = d.variants?.length > 0;
                    const basePrice = hasVariants ? Math.min(...d.variants.map((v) => Number(v.price))) : Number(d.price);
                    const canOrder = data.order_enabled && d.orderable && !ftMenuActive;
                    return (
                      <motion.div key={d.id} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                        className={`flex gap-4 rounded-xl transition-[background-color,opacity] ${hasAllergen(d) ? "bg-amber-50 ring-2 ring-amber-400 p-2 -mx-2" : filterAllergens.length ? "opacity-45" : ""}`}
                        data-testid={`public-dish-${d.id}`} data-highlighted={hasAllergen(d) ? "true" : "false"}>
                        {d.image_path && (
                          <div className="h-20 w-20 rounded-xl overflow-hidden bg-gray-100 shrink-0">
                            <img src={fileUrl(d.image_path)} alt={d.name} className="h-full w-full object-cover" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-baseline justify-between gap-3">
                            <h3 className="font-medium text-gray-900" data-no-translate>{tName(d)}</h3>
                            <span className="font-semibold whitespace-nowrap" style={{ color: t.accent }}>{hasVariants ? "da " : ""}{fmtMoney(basePrice, restaurant.currency_code, lang)}</span>
                          </div>
                          {tDesc(d) && <p className="text-sm text-gray-500 mt-0.5 leading-relaxed" data-no-translate>{tDesc(d)}</p>}
                          <AllergenBadges codes={d.allergens} />
                          <DietBadges codes={d.tags} />
                          {canOrder && (
                            <Button size="sm" onClick={() => (hasVariants || d.options?.length > 0) ? setConfigDish(d) : addToCart({ dish_id: d.id, name: d.name, variant: null, options: [], unit_price: basePrice, quantity: 1, notes: "", counts_capacity: d.counts_capacity !== false })}
                              className="mt-2 h-8 rounded-full text-white text-xs font-semibold active:scale-95 transition-transform" style={{ background: t.accent }} data-testid={`add-dish-cart-${d.id}`}>
                              <Plus className="h-3.5 w-3.5 mr-1" /> {(hasVariants || d.options?.length > 0) ? "Scegli e ordina" : "Aggiungi"}
                            </Button>
                          )}
                        </div>
                      </motion.div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>

        {data.pay_enabled && !ftMenuActive && (
          <div className="px-6 pb-4">
            <Button onClick={() => setPayOpen(true)} className="w-full h-12 rounded-full text-white font-semibold active:scale-95 transition-transform" style={{ background: t.accent }} data-testid="pay-bill-btn">
              <CreditCard className="h-5 w-5 mr-2" /> Paga il conto
            </Button>
          </div>
        )}

        {allergenCodesUsed.length > 0 && (
          <div className="px-6 py-6 border-t border-gray-100" data-testid="allergen-legend">
            <h3 className="text-xs font-bold uppercase tracking-wide text-amber-700 mb-2">Legenda allergeni</h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-600">
              {ALLERGENS.map((a) => (
                <li key={a.code} className={allergenCodesUsed.includes(a.code) ? "font-medium text-gray-800" : "text-gray-400"} data-testid={`legend-${a.code}`}>• {a.label}</li>
              ))}
            </ul>
            <p className="text-[11px] text-gray-400 mt-3 leading-relaxed" data-testid="allergen-legal-note">
              Informazioni sugli allergeni ai sensi del Reg. UE 1169/2011 e del D.Lgs. 231/2017. I piatti possono contenere tracce di altri allergeni per contaminazione crociata: per esigenze specifiche rivolgersi al personale prima dell'ordinazione.
            </p>
          </div>
        )}


        {data.branding && <AdSlot key={`bottom-${adsConsent}`} testid="adsense-bottom" />}

        <div className="px-6 py-8 border-t border-gray-100 flex flex-col items-center justify-center gap-3 text-gray-400 text-sm">
          {data.branding ? (
            <a href="/" className="flex flex-col items-center gap-2 text-center" data-testid="menuone-branding-footer">
              <div className="flex items-center gap-2"><span>Menù creato gratis con</span><Logo /></div>
              <span className="text-xs text-[#4a8a56] font-medium underline">Crea anche tu il tuo menù digitale</span>
            </a>
          ) : (
            <div className="flex items-center gap-2"><span>Creato con</span><Logo /></div>
          )}
          <a href="mailto:creamenuone@gmail.com" className="text-gray-400 hover:text-[#5aa469] transition-colors">creamenuone@gmail.com</a>
        </div>
      </div>

      {/* Floating cart */}
      {cartCount > 0 && (
        <div className="fixed bottom-5 inset-x-0 z-30 flex justify-center px-6">
          <Button onClick={() => setCartOpen(true)} className="max-w-md w-full h-13 py-3 rounded-full text-white font-semibold shadow-xl active:scale-95 transition-transform flex items-center justify-between px-5" style={{ background: t.accent }} data-testid="open-cart-btn">
            <span className="flex items-center gap-2"><ShoppingBag className="h-5 w-5" /> {cartCount} {cartCount === 1 ? "piatto" : "piatti"}</span>
            <span>{fmtMoney(cartTotal, restaurant.currency_code, lang)}</span>
          </Button>
        </div>
      )}

      {configDish && <DishConfigDialog dish={configDish} currency={restaurant.currency_code} accent={t.accent} onClose={() => setConfigDish(null)} onAdd={(line) => { addToCart(line); setConfigDish(null); }} />}

      {data.branding && <AdsConsentBanner onChange={setAdsConsent} />}
      <CartDialog open={cartOpen} onClose={() => setCartOpen(false)} cart={cart} setQty={setQty} removeLine={removeLine} total={cartTotal} currency={restaurant.currency_code} accent={t.accent} slug={slug} lang={lang} tables={data.tables || []} onOrdered={() => { setCart([]); setCartOpen(false); setOrderMode("table"); }}
        mode={orderMode} setMode={setOrderMode} takeawayEnabled={data.takeaway_enabled} slots={data.takeaway_slots || []} />

      <WaitlistDialog open={waitOpen} onClose={() => setWaitOpen(false)} accent={t.accent} slug={slug} tables={data.tables || []} onClaimed={() => loadMenu()} />
      <ReservationDialog open={resOpen} onClose={() => setResOpen(false)} accent={t.accent} slug={slug} />

      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="pay-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Paga il conto</DialogTitle>
            <DialogDescription>Inserisci l'importo del tuo conto e paga in sicurezza con Stripe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Importo ({restaurant.currency})</Label>
              <Input type="number" inputMode="decimal" step="0.01" min="0" value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Es. 42.50" data-testid="pay-amount-input" />
            </div>
            <div>
              <Label>Tavolo (facoltativo)</Label>
              <Input value={table} onChange={(e) => setTable(e.target.value)} className="mt-1.5 h-11 rounded-lg focus:border-[#5aa469]" placeholder="Es. 7" data-testid="pay-table-input" />
            </div>
            <Button onClick={submitPay} disabled={paying} className="w-full h-11 rounded-full text-white font-semibold" style={{ background: t.accent }} data-testid="pay-submit-btn">
              {paying ? <Loader2 className="h-4 w-4 animate-spin" /> : "Procedi al pagamento"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function DishConfigDialog({ dish, currency, accent, onClose, onAdd }) {
  const hasVariants = dish.variants?.length > 0;
  const [variant, setVariant] = useState(hasVariants ? dish.variants[0].label : null);
  const [selected, setSelected] = useState({}); // groupIndex -> choiceIndex
  const [notes, setNotes] = useState("");
  const [qty, setQty] = useState(1);

  const basePrice = hasVariants ? Number(dish.variants.find((v) => v.label === variant)?.price ?? dish.variants[0].price) : Number(dish.price);
  const optionExtras = useMemo(() => (dish.options || []).reduce((sum, g, gi) => {
    const ci = selected[gi];
    if (ci != null && g.choices[ci]) return sum + Number(g.choices[ci].price_delta || 0);
    return sum;
  }, 0), [selected, dish.options]);
  const unit = basePrice + optionExtras;

  const confirm = () => {
    for (let gi = 0; gi < (dish.options || []).length; gi++) {
      if (dish.options[gi].required && selected[gi] == null) { toast.error(`Scegli: ${dish.options[gi].name}`); return; }
    }
    const chosenLabels = (dish.options || []).map((g, gi) => (selected[gi] != null ? `${g.name}: ${g.choices[selected[gi]].label}` : null)).filter(Boolean);
    const name = dish.name;
    onAdd({ dish_id: dish.id, name, variant: hasVariants ? variant : null, options: chosenLabels, unit_price: unit, quantity: qty, notes, counts_capacity: dish.counts_capacity !== false });
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm max-h-[85vh] overflow-y-auto" data-testid="dish-config-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl" data-no-translate>{dish.name}</DialogTitle>
          <DialogDescription>Personalizza il piatto e aggiungilo all'ordine.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {hasVariants && (
            <div>
              <Label>Scegli la variante</Label>
              <div className="mt-2 space-y-2">
                {dish.variants.map((v) => (
                  <button key={v.label} onClick={() => setVariant(v.label)} className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors" style={variant === v.label ? { borderColor: accent, background: `${accent}12` } : { borderColor: "#e5e7eb" }} data-testid={`variant-choice-${v.label}`}>
                    <span className="text-sm font-medium">{v.label}</span>
                    <span className="text-sm" style={{ color: accent }}>{fmtMoney(v.price, currency)}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
          {(dish.options || []).map((g, gi) => (
            <div key={`${g.name}-${gi}`}>
              <Label>{g.name} {g.required && <span className="text-red-500">*</span>}</Label>
              <div className="mt-2 space-y-2">
                {g.choices.map((c, ci) => (
                  <button key={`${c.label}-${ci}`} onClick={() => setSelected((s) => ({ ...s, [gi]: s[gi] === ci ? undefined : ci }))} className="w-full flex items-center justify-between px-4 py-2.5 rounded-lg border transition-colors" style={selected[gi] === ci ? { borderColor: accent, background: `${accent}12` } : { borderColor: "#e5e7eb" }} data-testid={`option-choice-${gi}-${ci}`}>
                    <span className="text-sm">{c.label}</span>
                    {Number(c.price_delta) !== 0 && <span className="text-sm text-gray-500">+{fmtMoney(c.price_delta, currency)}</span>}
                  </button>
                ))}
              </div>
            </div>
          ))}
          <div>
            <Label>Note (facoltative)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. senza cipolla" className="mt-1.5 rounded-lg" data-testid="dish-config-notes" />
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Button variant="outline" size="icon" onClick={() => setQty((q) => Math.max(1, q - 1))} className="rounded-full h-9 w-9"><Minus className="h-4 w-4" /></Button>
              <span className="font-semibold w-6 text-center" data-testid="config-qty">{qty}</span>
              <Button variant="outline" size="icon" onClick={() => setQty((q) => q + 1)} className="rounded-full h-9 w-9"><Plus className="h-4 w-4" /></Button>
            </div>
            <Button onClick={confirm} className="rounded-full text-white font-semibold" style={{ background: accent }} data-testid="dish-config-add">Aggiungi · {fmtMoney(unit * qty, currency)}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CartDialog({ open, onClose, cart, setQty, removeLine, total, currency, accent, slug, onOrdered, mode = "table", setMode, takeawayEnabled = false, slots: initialSlots = [], tables = [], lang = "it" }) {
  const [table, setTable] = useState("");
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [phone, setPhone] = useState("");
  const [pickup, setPickup] = useState("");
  const [notes, setNotes] = useState("");
  const [fulfillment, setFulfillment] = useState("pickup");
  const [payOnline, setPayOnline] = useState(false);
  const [email, setEmail] = useState("");
  const [addr, setAddr] = useState({ street: "", number: "", city: "", zip: "", intercom: "", notes: "" });
  const setA = (k) => (e) => setAddr((a) => ({ ...a, [k]: e.target.value }));
  const [sending, setSending] = useState(false);
  const [cap, setCap] = useState(null);
  const takeaway = takeawayEnabled && mode === "takeaway";
  const fmt = (v) => fmtMoney(v, currency);
  const cartQty = cart.reduce((s, l) => s + (l.counts_capacity === false ? 0 : l.quantity), 0);

  const [pickupDate, setPickupDate] = useState(() => new Date().toISOString().slice(0, 10));
  useEffect(() => {
    if (!open || !takeaway) return;
    let stop = false;
    const load = () => api.get(`/public/takeaway/${slug}/slots?date=${pickupDate}`).then((r) => { if (!stop) setCap(r.data); }).catch(() => {});
    load();
    const id = setInterval(load, 20000);
    return () => { stop = true; clearInterval(id); };
  }, [open, takeaway, slug, pickupDate]);
  const advanceDays = cap?.advance_days ?? 0;
  const maxDate = new Date(Date.now() + advanceDays * 86400000).toISOString().slice(0, 10);

  const itemsMode = cap?.mode === "items" && cap?.limit > 0;
  const deliveryCfg = cap?.delivery?.enabled ? cap.delivery : null;
  const isDelivery = takeaway && !!deliveryCfg && fulfillment === "delivery";
  const deliveryFee = isDelivery ? Number(deliveryCfg.fee || 0) : 0;
  const belowMin = isDelivery && deliveryCfg.min_order > 0 && total < deliveryCfg.min_order;
  const payCfg = cap?.payment || { mode: "later" };
  const payNow = takeaway && (payCfg.mode === "online" || (payCfg.mode === "choice" && payOnline));
  const slotList = cap ? cap.slots : initialSlots.map((t) => ({ t, free: null }));
  const slotOk = (s) => s.free === null || s.free > 0;
  const slots = slotList.filter(slotOk).map((s) => s.t);
  const chosen = slotList.find((s) => s.t === pickup);
  const spills = itemsMode && chosen && chosen.free !== null && cartQty > chosen.free;
  useEffect(() => { if (pickup && cap && !slotList.some((s) => s.t === pickup && slotOk(s))) setPickup(""); }, [cap]); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async () => {
    if (cart.length === 0) return;
    if (takeaway) {
      if (!name.trim() || !surname.trim()) { toast.error("Inserisci nome e cognome"); return; }
      if (!phone.trim()) { toast.error("Inserisci il telefono"); return; }
      if (!pickup) { toast.error(isDelivery ? "Scegli l'orario di consegna" : "Scegli l'orario di ritiro"); return; }
      if (isDelivery && (!addr.street.trim() || !addr.number.trim() || !addr.city.trim())) { toast.error("Inserisci via, numero civico e città"); return; }
      if (payNow && !/^\S+@\S+\.\S+$/.test(email.trim())) { toast.error("Inserisci un'email valida per ricevere la ricevuta"); return; }
      if (belowMin) { toast.error(`Ordine minimo per la consegna: ${fmt(deliveryCfg.min_order)}`); return; }
    }
    setSending(true);
    try {
      const items = cart.map((l) => ({ dish_id: l.dish_id, name: l.name, quantity: l.quantity, variant: l.variant, options: l.options, unit_price: l.unit_price, notes: l.notes }));
      const payload = takeaway
        ? { items, notes, type: "takeaway", customer_name: name, customer_surname: surname, phone, pickup_time: pickup, pickup_date: pickupDate, fulfillment: isDelivery ? "delivery" : "pickup", delivery_address: isDelivery ? addr : null, pay_online: payNow, origin_url: window.location.origin, lang, email: email.trim() }
        : { items, table, customer_name: name, notes };
      const { data } = await api.post(`/public/order/${slug}`, payload);
      if (data.pending_payment && data.checkout_url) { window.location.href = data.checkout_url; return; }
      const dayTxt = data.pickup_date && data.pickup_date !== new Date().toISOString().slice(0, 10) ? `${new Date(data.pickup_date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} ` : "";
      toast.success(takeaway ? (isDelivery ? `Ordine #${data.order_no} confermato! Consegna ${dayTxt}alle ${data.ready_at && data.ready_at !== data.pickup_time ? data.ready_at : data.pickup_time} a ${addr.street} ${addr.number}, ${addr.city}.` : `Prenotazione #${data.order_no} confermata! Ritiro ${dayTxt}alle ${data.ready_at && data.ready_at !== data.pickup_time ? `${data.ready_at} (i primi piatti alle ${data.pickup_time})` : data.pickup_time}.`) : `Ordine #${data.order_no} inviato! Il locale è stato avvisato.`);
      onOrdered();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="cart-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{takeaway ? "Prenota da asporto" : "Il tuo ordine"}</DialogTitle>
          <DialogDescription>{takeaway ? "Indica i tuoi dati e l'orario in cui passerai a ritirare." : "Controlla i piatti e invia l'ordine al tavolo."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {takeawayEnabled && (
            <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-gray-100" data-testid="order-mode-switch">
              {[["table", "Al tavolo"], ["takeaway", "Asporto"]].map(([m, label]) => (
                <button key={m} type="button" onClick={() => setMode(m)} className="h-9 rounded-full text-sm font-semibold transition-colors" style={mode === m ? { background: accent, color: "#fff" } : { color: "#4b5563" }} data-testid={`order-mode-${m}`}>{label}</button>
              ))}
            </div>
          )}
          {cart.map((l) => (
            <div key={l.key} className="flex items-start gap-3 border-b border-gray-100 pb-3" data-testid={`cart-line-${l.dish_id}`}>
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900" data-no-translate>{l.name}{l.variant ? ` · ${l.variant}` : ""}</p>
                {l.options?.length > 0 && <p className="text-xs text-gray-500">{l.options.join(", ")}</p>}
                {l.notes && <p className="text-xs text-gray-400">Note: {l.notes}</p>}
                <div className="flex items-center gap-2 mt-1.5">
                  <Button variant="outline" size="icon" onClick={() => setQty(l.key, -1)} className="rounded-full h-7 w-7"><Minus className="h-3 w-3" /></Button>
                  <span className="text-sm w-5 text-center">{l.quantity}</span>
                  <Button variant="outline" size="icon" onClick={() => setQty(l.key, 1)} className="rounded-full h-7 w-7"><Plus className="h-3 w-3" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => removeLine(l.key)} className="text-red-500 h-7 w-7 ml-1"><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              </div>
              <span className="font-semibold whitespace-nowrap" style={{ color: accent }}>{fmtMoney(l.unit_price * l.quantity, currency)}</span>
            </div>
          ))}
          {cart.length === 0 && <p className="text-gray-400 text-sm py-6 text-center">Il carrello è vuoto.</p>}
          {takeaway ? (
            <div className="space-y-3" data-testid="takeaway-fields">
              {deliveryCfg && (
                <div>
                  <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-gray-100" data-testid="fulfillment-switch">
                    {[["pickup", "Ritiro al locale"], ["delivery", "Consegna a domicilio"]].map(([v, l]) => (
                      <button key={v} type="button" onClick={() => setFulfillment(v)} className="h-9 rounded-full text-sm font-medium transition-colors" style={fulfillment === v ? { background: accent, color: "#fff" } : { color: "#374151" }} data-testid={`fulfillment-${v}`}>{l}</button>
                    ))}
                  </div>
                  {isDelivery && <p className="text-xs text-gray-500 mt-1.5" data-testid="delivery-info">{deliveryFee > 0 ? `Costo consegna ${fmt(deliveryFee)}` : "Consegna gratuita"}{deliveryCfg.min_order > 0 && ` · ordine minimo ${fmt(deliveryCfg.min_order)}`}{deliveryCfg.note && ` · ${deliveryCfg.note}`}</p>}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome" className="mt-1.5 h-11 rounded-lg" data-testid="takeaway-name-input" /></div>
                <div><Label>Cognome</Label><Input value={surname} onChange={(e) => setSurname(e.target.value)} placeholder="Cognome" className="mt-1.5 h-11 rounded-lg" data-testid="takeaway-surname-input" /></div>
              </div>
              <div><Label>Telefono</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Es. 333 1234567" className="mt-1.5 h-11 rounded-lg" data-testid="takeaway-phone-input" /></div>
              {payCfg.mode !== "later" && <div><Label>Email {payNow ? "(per la ricevuta)" : "(facoltativa)"}</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@email.it" className="mt-1.5 h-11 rounded-lg" data-testid="takeaway-email-input" /></div>}
              {isDelivery && (
                <div className="space-y-2 rounded-xl border border-gray-200 p-3" data-testid="delivery-address-fields">
                  <p className="text-sm font-semibold">Indirizzo di consegna</p>
                  <div className="grid grid-cols-[1fr_90px] gap-2">
                    <div><Label>Via / Piazza</Label><Input value={addr.street} onChange={setA("street")} placeholder="Via Roma" className="mt-1 h-11 rounded-lg" data-testid="delivery-street" /></div>
                    <div><Label>N. civico</Label><Input value={addr.number} onChange={setA("number")} placeholder="12" className="mt-1 h-11 rounded-lg" data-testid="delivery-number" /></div>
                  </div>
                  <div className="grid grid-cols-[1fr_110px] gap-2">
                    <div><Label>Città</Label><Input value={addr.city} onChange={setA("city")} placeholder="Firenze" className="mt-1 h-11 rounded-lg" data-testid="delivery-city" /></div>
                    <div><Label>CAP</Label><Input value={addr.zip} onChange={setA("zip")} placeholder="50100" inputMode="numeric" className="mt-1 h-11 rounded-lg" data-testid="delivery-zip" /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div><Label>Citofono / Scala / Piano</Label><Input value={addr.intercom} onChange={setA("intercom")} placeholder="Rossi, 3° piano" className="mt-1 h-11 rounded-lg" data-testid="delivery-intercom" /></div>
                    <div><Label>Indicazioni per il fattorino</Label><Input value={addr.notes} onChange={setA("notes")} placeholder="Cancello verde" className="mt-1 h-11 rounded-lg" data-testid="delivery-notes" /></div>
                  </div>
                  {belowMin && <p className="text-xs text-red-600" data-testid="delivery-below-min">Ordine minimo per la consegna: {fmt(deliveryCfg.min_order)} (nel carrello hai {fmt(total)}).</p>}
                </div>
              )}
              {payCfg.mode === "choice" && (
                <div data-testid="pay-choice">
                  <Label>Pagamento</Label>
                  <div className="grid grid-cols-2 gap-2 p-1 rounded-full bg-gray-100 mt-1.5">
                    {[[false, isDelivery ? "Paga alla consegna" : "Paga al ritiro"], [true, "Paga online ora"]].map(([v, l]) => (
                      <button key={String(v)} type="button" onClick={() => setPayOnline(v)} className="h-9 rounded-full text-sm font-medium transition-colors" style={payOnline === v ? { background: accent, color: "#fff" } : { color: "#374151" }} data-testid={`pay-choice-${v ? "online" : "later"}`}>{l}</button>
                    ))}
                  </div>
                </div>
              )}
              {payCfg.mode === "online" && <p className="text-xs text-gray-600 rounded-lg bg-gray-50 px-3 py-2 inline-flex items-center gap-1.5" data-testid="pay-online-required"><CreditCard className="h-3.5 w-3.5" />Questo locale richiede il pagamento online: dopo la conferma verrai portato alla pagina di pagamento sicura.</p>}
              <div>
                {advanceDays > 0 && (
                  <div className="mb-3">
                    <Label>{isDelivery ? "Giorno di consegna" : "Giorno di ritiro"}</Label>
                    <Input type="date" value={pickupDate} min={cap?.today || new Date().toISOString().slice(0, 10)} max={maxDate} onChange={(e) => { setPickupDate(e.target.value); setPickup(""); }} className="mt-1.5 h-11 rounded-lg" data-testid="takeaway-date-input" />
                    <p className="text-xs text-gray-400 mt-1">Puoi prenotare fino a {advanceDays} giorni prima.</p>
                  </div>
                )}
                <Label>{isDelivery ? "Orario di consegna" : "Orario di ritiro"}</Label>
                {itemsMode && <p className="text-xs text-gray-500 mt-1" data-testid="takeaway-capacity-hint">Il locale prepara al massimo {cap.limit} pezzi per orario · nel carrello hai {cartQty} {cartQty === 1 ? "pezzo" : "pezzi"}. Il numero accanto all'orario indica i pezzi ancora disponibili.</p>}
                {cap?.closed ? (
                  <p className="text-sm text-red-600 mt-1.5" data-testid="takeaway-closed">Il locale è chiuso in questa data: scegli un altro giorno.</p>
                ) : slots.length === 0 ? (
                  <p className="text-sm text-amber-600 mt-1.5" data-testid="takeaway-no-slots">Nessun orario disponibile per {pickupDate === (cap?.today || new Date().toISOString().slice(0, 10)) ? "oggi" : "questo giorno"}.</p>
                ) : (
                  <div className="mt-1.5 flex flex-wrap gap-2 max-h-36 overflow-y-auto" data-testid="takeaway-slots">
                    {slotList.filter(slotOk).map((s) => (
                      <button key={s.t} type="button" onClick={() => setPickup(s.t)} className="px-3 h-9 rounded-full text-sm font-medium border transition-colors" style={pickup === s.t ? { background: accent, color: "#fff", borderColor: accent } : { borderColor: "#e5e7eb", color: "#374151" }} data-testid={`takeaway-slot-${s.t.replace(":", "")}`}>
                        {s.t}{s.free !== null && <span className="ml-1 text-[10px] opacity-70" data-testid={`takeaway-free-${s.t.replace(":", "")}`}>· {s.free}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {spills && <p className="text-xs text-amber-600 mt-1.5" data-testid="takeaway-spill-hint">Alle {pickup} restano {chosen.free} {chosen.free === 1 ? "pezzo" : "pezzi"}: gli altri {cartQty - chosen.free} passano all'orario successivo, quindi il ritiro completo slitta un po' più tardi. Ti confermiamo l'orario dopo l'invio.</p>}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tavolo</Label>
                {tables?.length ? (
                  <select value={table} onChange={(e) => setTable(e.target.value)} className="mt-1.5 h-11 w-full rounded-lg border border-gray-200 bg-white px-3 text-sm" data-testid="cart-table-select">
                    <option value="">Il tuo tavolo…</option>
                    {tables.map((tb) => <option key={tb.id} value={tb.name} disabled={tb.status !== "occupied"}>{tb.name}{tb.status !== "occupied" ? " (libero: prenotalo prima)" : ""}</option>)}
                  </select>
                ) : (
                  <Input value={table} onChange={(e) => setTable(e.target.value)} placeholder="Es. 7" className="mt-1.5 h-11 rounded-lg" data-testid="cart-table-input" />
                )}
              </div>
              <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Il tuo nome" className="mt-1.5 h-11 rounded-lg" data-testid="cart-name-input" /></div>
            </div>
          )}
          <div><Label>Note ordine</Label><Textarea value={notes} onChange={(e) => setNotes(e.target.value)} className="mt-1.5 rounded-lg" data-testid="cart-notes-input" /></div>
          <div className="flex items-center justify-between pt-1">
            <span className="font-display font-bold text-xl" data-testid="cart-total">Totale: {fmtMoney(total + deliveryFee, currency)}{deliveryFee > 0 && <span className="block text-xs font-sans font-normal text-gray-500" data-testid="cart-delivery-fee">incl. consegna {fmt(deliveryFee)}</span>}</span>
            {takeaway && <span className="text-xs text-gray-500">Si paga al ritiro</span>}
          </div>
          <Button onClick={submit} disabled={sending || cart.length === 0 || (takeaway && slots.length === 0)} className="w-full h-12 rounded-full text-white font-semibold" style={{ background: accent }} data-testid="submit-order-btn">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : payNow ? "Conferma e paga online" : takeaway ? "Conferma prenotazione" : "Invia ordine"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ReservationDialog({ open, onClose, accent, slug }) {
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [avail, setAvail] = useState(null);
  const [time, setTime] = useState("");
  const [party, setParty] = useState(2);
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!open) return;
    setAvail(null);
    api.get(`/public/reservations/${slug}/availability?date=${date}`).then((r) => setAvail(r.data)).catch(() => setAvail({ times: [] }));
  }, [open, date, slug]);

  const maxDate = new Date(Date.now() + (avail?.advance_days || 60) * 86400000).toISOString().slice(0, 10);
  const submit = async () => {
    if (!name.trim() || !phone.trim()) { toast.error("Inserisci nome e telefono"); return; }
    if (!time) { toast.error("Scegli un orario"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/public/reservations/${slug}`, { name, phone, email, date, time, party_size: party, notes });
      setDone(data.status);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };
  const reset = () => { setDone(null); setTime(""); setNotes(""); onClose(); };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="sm:max-w-md max-h-[88vh] overflow-y-auto" data-testid="reservation-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">Prenota un tavolo</DialogTitle>
          <DialogDescription>Scegli giorno, orario e numero di persone. Puoi prenotare fino a {avail?.advance_days || 60} giorni prima.</DialogDescription>
        </DialogHeader>
        {done ? (
          <div className="text-center py-6 space-y-2" data-testid="reservation-done">
            <CalendarCheck className="h-10 w-10 mx-auto" style={{ color: accent }} />
            <p className="font-semibold">{done === "confirmed" ? "Tavolo confermato!" : "Richiesta inviata!"}</p>
            <p className="text-sm text-gray-500">{new Date(date + "T12:00:00").toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })} alle {time} · {party} {party === 1 ? "persona" : "persone"}</p>
            {done !== "confirmed" && <p className="text-xs text-gray-400">Il locale confermerà la prenotazione: verrai contattato al numero indicato.</p>}
            {email && <p className="text-xs text-gray-400" data-testid="reservation-email-note">Ti abbiamo inviato un'email di riepilogo a {email}{done === "confirmed" ? " e riceverai un promemoria il giorno prima." : "."}</p>}
            <Button onClick={reset} className="rounded-full text-white mt-2" style={{ background: accent }} data-testid="reservation-close-btn">Chiudi</Button>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Giorno</Label><Input type="date" value={date} min={today} max={maxDate} onChange={(e) => { setDate(e.target.value); setTime(""); }} className="mt-1.5 h-11 rounded-lg" data-testid="reservation-date-input" /></div>
              <div><Label>Persone</Label><Input type="number" min="1" max={avail?.max_party || 12} value={party} onChange={(e) => setParty(Math.max(1, Number(e.target.value) || 1))} className="mt-1.5 h-11 rounded-lg" data-testid="reservation-party-input" /></div>
            </div>
            <div>
              <Label>Orario</Label>
              {avail === null ? <Loader2 className="h-4 w-4 animate-spin mt-2 text-gray-400" /> : avail.closed ? <p className="text-sm text-red-600 mt-1.5" data-testid="reservation-closed">Il locale è chiuso in questa data: scegli un altro giorno.</p> : avail.times.length === 0 ? <p className="text-sm text-amber-600 mt-1.5" data-testid="reservation-no-times">Nessun orario disponibile per questo giorno.</p> : (
                <div className="mt-1.5 flex flex-wrap gap-2 max-h-36 overflow-y-auto" data-testid="reservation-times">
                  {avail.times.map((s) => {
                    const full = s.seats_left !== null && s.seats_left < party;
                    return <button key={s.t} type="button" disabled={full} onClick={() => setTime(s.t)} className="px-3 h-9 rounded-full text-sm font-medium border transition-colors disabled:opacity-40 disabled:line-through" style={time === s.t ? { background: accent, color: "#fff", borderColor: accent } : { borderColor: "#e5e7eb", color: "#374151" }} data-testid={`reservation-time-${s.t.replace(":", "")}`}>{s.t}</button>;
                  })}
                </div>
              )}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome" className="mt-1.5 h-11 rounded-lg" data-testid="reservation-name-input" /></div>
              <div><Label>Telefono</Label><Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="333 1234567" className="mt-1.5 h-11 rounded-lg" data-testid="reservation-phone-input" /></div>
            </div>
            <div><Label>Email (facoltativa, per conferma e promemoria)</Label><Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="nome@esempio.it" className="mt-1.5 h-11 rounded-lg" data-testid="reservation-email-input" /></div>
            <div><Label>Note (facoltative)</Label><Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Es. seggiolone, compleanno, allergie" className="mt-1.5 h-11 rounded-lg" data-testid="reservation-notes-input" /></div>
            <Button onClick={submit} disabled={sending || !time} className="w-full h-12 rounded-full text-white font-semibold" style={{ background: accent }} data-testid="reservation-submit-btn">{sending ? "Invio…" : "Prenota"}</Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}


function WaitlistDialog({ open, onClose, accent, slug, tables = [], onClaimed }) {
  const [name, setName] = useState("");
  const [party, setParty] = useState(2);
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [picked, setPicked] = useState(null);
  const freeTables = tables.filter((t) => t.status !== "occupied");

  const claim = async () => {
    if (!name.trim()) { toast.error("Inserisci il tuo nome"); return; }
    setSending(true);
    try {
      const { data } = await api.post(`/public/table/${slug}/claim`, { table_id: picked.id, name, party_size: Number(party) || 1 });
      toast.success(`Tavolo ${data.table.name} prenotato! Accomodati: ora puoi ordinare dal tavolo.`);
      onClaimed?.(data.table);
      onClose(); setPicked(null); setName("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); onClaimed?.(null); }
    finally { setSending(false); }
  };

  const submit = async () => {
    if (!name.trim()) { toast.error("Inserisci il tuo nome"); return; }
    setSending(true);
    try {
      await api.post(`/public/waitlist/${slug}`, { name, party_size: Number(party) || 1, phone });
      toast.success("Sei in lista d'attesa! Il locale ti avviserà.");
      onClose();
      setName(""); setPhone("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-sm max-h-[90vh] overflow-y-auto" data-testid="waitlist-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl">{freeTables.length ? "Scegli un tavolo" : "Lista d'attesa"}</DialogTitle>
          <DialogDescription>{freeTables.length ? "I tavoli verdi sono liberi: prenotane uno e accomodati." : tables.length ? "Tutti i tavoli sono occupati: lascia i tuoi dati e ti chiamiamo appena se ne libera uno." : "Lascia i tuoi dati: il locale ti chiamerà appena si libera un tavolo."}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          {tables.length > 0 && (
            <div className="grid grid-cols-3 gap-2" data-testid="public-tables-grid">
              {tables.map((t) => {
                const occ = t.status === "occupied";
                const sel = picked?.id === t.id;
                return (
                  <button key={t.id} type="button" disabled={occ} onClick={() => setPicked(t)} className={`rounded-xl p-3 text-left border-2 transition-colors ${occ ? "bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed" : sel ? "text-white" : "bg-[#5aa469]/10 border-[#5aa469] text-gray-900"}`} style={sel ? { background: accent, borderColor: accent } : undefined} data-testid={`public-table-${t.id}`}>
                    <div className="font-display font-bold text-lg leading-none">{t.name}</div>
                    <div className="text-[11px] mt-1 opacity-80">{t.seats} posti · {occ ? "occupato" : "libero"}</div>
                  </button>
                );
              })}
            </div>
          )}
          <div><Label>Nome</Label><Input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="waitlist-name-input" /></div>
          <div><Label>Numero di persone</Label><Input type="number" min="1" value={party} onChange={(e) => setParty(e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="waitlist-party-input" /></div>
          {freeTables.length > 0 ? (
            <Button onClick={claim} disabled={sending || !picked} className="w-full h-11 rounded-full text-white font-semibold" style={{ background: accent }} data-testid="claim-table-btn">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : picked ? `Prenota il tavolo ${picked.name}` : "Scegli un tavolo verde"}
            </Button>
          ) : (
            <>
              <div><Label>Telefono (facoltativo)</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="mt-1.5 h-11 rounded-lg" data-testid="waitlist-phone-input" /></div>
              <Button onClick={submit} disabled={sending} className="w-full h-11 rounded-full text-white font-semibold" style={{ background: accent }} data-testid="waitlist-submit-btn">
                {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Mettimi in lista"}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
